import type {
  StaffCatalogPlaceCandidate,
  StaffCatalogPlaceCorrection,
  StaffCatalogPlaceSearchResult,
  StaffCatalogPlaceView,
} from "@lafayette/schemas";
import { useEffect, useRef, useState } from "react";

import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { Notice } from "../../ui/notice";
import { SectionCard } from "../../ui/section-card";
import { StatusMessage } from "../../ui/status-message";
import type { SaveMessage } from "../commands";
import { factList, hintText, listRow, plainList, subheading } from "../components/styles";
import { formatDateTime } from "../labels";
import type { ItemSectionContext } from "../sections/section-support";
import { describePlaceOutcome, usePlaceCommand } from "./commands";
import { ClearDialog } from "./components/PlaceDialogs";
import { PlaceSearchPanel } from "./components/PlaceSearchPanel";
import { describePlaceError } from "./errors";
import {
  decisionLabels,
  lookupKindLabels,
  lookupOutcomeLabels,
  placesErrorSentences,
  reasonLabels,
} from "./labels";
import { PlaceBadge } from "./parts";
import { usePlaceView } from "./queries";
import { errorSentence, viewBadges } from "./state";
import { placeId } from "./styles";

/**
 * The Place section of the item page: the state of the match, the actions, the search panel and the
 * history. Staff with `catalog.edit` search, match, correct, clear and verify. Other staff who can read
 * the catalog see the state and the history and no action button. Provider content is held only by the
 * search panel while it is open; this section shows the stored place id, codes and dates.
 */
export function PlaceSection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, capabilities } = ctx;
  const itemId = detail.item.id;
  const query = usePlaceView(itemId, detail.item.version, capabilities.read);
  const place = usePlaceCommand(itemId);
  const [searching, setSearching] = useState(false);
  const [dialog, setDialog] = useState<"clear" | "noPlace" | null>(null);
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const [queued, setQueued] = useState(false);
  // A match or a removal closes the panel and the dialog, and the focus would fall to the page body. The
  // section heading takes it (the same target as the jump links), once no dialog is left to give it back.
  const focusHeading = useRef(false);
  // No dependency list on purpose: the flag is a ref, so only a render can notice it.
  useEffect(() => {
    if (focusHeading.current && dialog === null) {
      focusHeading.current = false;
      document.getElementById("place-title")?.focus();
    }
  });

  const description =
    "Match this item to its Google place so that other tools can refer to it. Lafayette keeps only the place id.";

  if (query.isPending) {
    return (
      <SectionCard id="place" title="Place" description={description}>
        <AsyncState kind="loading" title="Loading the place" message="Reading the place match." />
      </SectionCard>
    );
  }
  if (query.isError) {
    return (
      <SectionCard id="place" title="Place" description={description}>
        <AsyncState
          kind="error"
          title="The place could not be loaded"
          message={describePlaceError(query.error, { action: "read" }).message}
          onRetry={() => void query.refetch()}
        />
      </SectionCard>
    );
  }

  const view: StaffCatalogPlaceView = query.data;
  const badges = viewBadges(view);
  const busy = place.pending || ctx.command.pending;
  const hasCurrent = view.current !== null;
  const matchState = view.state.matchState;
  const canAct = !readOnly;

  async function select(
    result: StaffCatalogPlaceSearchResult,
    candidate: StaffCatalogPlaceCandidate,
    correction: StaffCatalogPlaceCorrection | undefined,
  ): Promise<string | null> {
    const outcome = await place.run(
      "select",
      {
        expectedVersion: detail.item.version,
        searchLookupId: result.searchLookupId,
        placeId: candidate.placeId,
        ...(correction === undefined ? {} : { correction }),
      },
      { lookupAvailable: view.lookupAvailable },
    );
    if (!outcome.ok) return describePlaceOutcome(outcome, "").text;
    // The choice is saved: the list of results is dropped with the panel.
    focusHeading.current = true;
    setSearching(false);
    setMessage({
      tone: "success",
      text: correction === undefined ? "Place matched." : "Match corrected.",
    });
    return null;
  }

  async function clear(
    noPlace: boolean,
    correction: StaffCatalogPlaceCorrection,
  ): Promise<string | null> {
    const outcome = await place.run(
      "clear",
      {
        expectedVersion: detail.item.version,
        reasonCode: correction.reasonCode,
        reason: correction.reason,
        ...(noPlace ? { noPlace: true } : {}),
      },
      { lookupAvailable: view.lookupAvailable },
    );
    if (!outcome.ok) return describePlaceOutcome(outcome, "").text;
    focusHeading.current = true;
    setSearching(false);
    setMessage({
      tone: "success",
      text: noPlace ? "Marked as having no physical place." : "The match was removed.",
    });
    return null;
  }

  async function verify() {
    setMessage(null);
    setQueued(false);
    const outcome = await place.run("verify", {}, { lookupAvailable: view.lookupAvailable });
    setQueued(outcome.ok);
    setMessage(
      describePlaceOutcome(
        outcome,
        "Verification queued. It runs in the background. Press Refresh to read the result.",
      ),
    );
  }

  return (
    <SectionCard id="place" title="Place" description={description}>
      <dl className={factList}>
        <div>
          <dt>State</dt>
          <dd className="flex flex-wrap gap-2">
            {badges.map((badge) => (
              <PlaceBadge key={badge.key} badge={badge} />
            ))}
          </dd>
        </div>
        <div>
          <dt>Place id</dt>
          <dd>
            {view.current === null ? (
              "None"
            ) : (
              <code className={placeId}>{view.current.placeId}</code>
            )}
          </dd>
        </div>
        {view.current === null ? null : (
          <div>
            <dt>Stored</dt>
            <dd>{formatDateTime(view.current.observedAt)}</dd>
          </div>
        )}
        {view.state.nextVerifyAt === null ? null : (
          <div>
            <dt>Next check</dt>
            <dd>{formatDateTime(view.state.nextVerifyAt)}</dd>
          </div>
        )}
      </dl>
      <ul className={cn(plainList, hintText)}>
        {badges.map((badge) => (
          <li key={badge.key}>{badge.explanation}</li>
        ))}
      </ul>

      {(view.state.refreshState === "STALE" || view.state.refreshState === "ERROR") &&
      view.state.lastErrorCode !== null ? (
        <Notice tone="warning" role="note">
          The last place check failed ({placesErrorSentences[view.state.lastErrorCode]}). This item
          is not changed and customers still see it as before.
        </Notice>
      ) : null}

      {!view.lookupAvailable ? (
        <Notice tone="warning" role="note">
          Place lookup is not configured. You can read the match and remove it. Search and
          verification are off.
        </Notice>
      ) : null}

      {readOnly ? (
        <Notice role="note">
          {detail.item.status === "ARCHIVED" && capabilities.edit
            ? "This item is archived. Its place cannot be changed."
            : "You can read the place state. Only a catalog editor can change it."}
        </Notice>
      ) : (
        <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0 sm:flex-row sm:flex-wrap">
          <legend className="sr-only">Place actions</legend>
          <Button
            type="button"
            aria-expanded={searching}
            disabled={busy || !view.lookupAvailable}
            onClick={() => {
              setMessage(null);
              setSearching((open) => !open);
            }}
          >
            Find the place
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={busy || !view.lookupAvailable || matchState !== "MATCHED"}
            onClick={() => void verify()}
          >
            Verify now
          </Button>
          {hasCurrent ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setDialog("clear")}
            >
              Clear match
            </Button>
          ) : null}
          {matchState === "NO_PLACE" ? null : (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setDialog("noPlace")}
            >
              Mark as no physical place
            </Button>
          )}
        </fieldset>
      )}

      <StatusMessage message={message} />
      {queued ? (
        <div>
          <Button type="button" variant="secondary" onClick={() => void query.refetch()}>
            Refresh
          </Button>
        </div>
      ) : null}

      {canAct && searching ? (
        <PlaceSearchPanel
          item={detail.item}
          hasCurrent={hasCurrent}
          lookupAvailable={view.lookupAvailable}
          onSelect={select}
          onClose={() => setSearching(false)}
        />
      ) : null}

      {dialog === null ? null : (
        <ClearDialog
          noPlace={dialog === "noPlace"}
          onConfirm={(correction) => clear(dialog === "noPlace", correction)}
          onClose={() => setDialog(null)}
        />
      )}

      <PlaceHistory view={view} />
    </SectionCard>
  );
}

/** The decisions and provider calls of the item. They hold ids, codes and dates and no provider text. */
function PlaceHistory({ view }: Readonly<{ view: StaffCatalogPlaceView }>) {
  return (
    <div className="grid min-w-0 gap-2">
      <h3 className={subheading}>Match history</h3>
      {view.matches.length === 0 ? (
        <p className={hintText}>No decisions yet.</p>
      ) : (
        <ol className={plainList}>
          {view.matches.map((match) => (
            <li key={match.id} className={cn(listRow, "grid gap-1")}>
              <p className="m-0">
                <strong>{decisionLabels[match.decision]}</strong>
                {match.reasonCode === null ? null : ` · ${reasonLabels[match.reasonCode]}`}
              </p>
              <p className={hintText}>
                {formatDateTime(match.createdAt)} · item version {match.itemVersion}
              </p>
              {match.placeId === null ? null : (
                <p className="m-0">
                  Place id <code className={placeId}>{match.placeId}</code>
                </p>
              )}
              {match.previousPlaceId === null ? null : (
                <p className="m-0">
                  Replaced place id <code className={placeId}>{match.previousPlaceId}</code>
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
      <h3 className={subheading}>Lookup history</h3>
      {view.lookups.length === 0 ? (
        <p className={hintText}>No lookups yet.</p>
      ) : (
        <ol className={plainList}>
          {view.lookups.map((lookup) => (
            <li key={lookup.id} className={cn(listRow, "grid gap-1")}>
              <p className="m-0">
                <strong>{lookupKindLabels[lookup.lookupKind]}</strong>
                {" · "}
                {lookup.outcome === null ? "Started" : lookupOutcomeLabels[lookup.outcome]}
                {lookup.errorCode === null
                  ? ""
                  : ` · ${errorSentence(lookup.errorCode).replace(/^Error: /, "")}`}
              </p>
              <p className={hintText}>
                {formatDateTime(lookup.createdAt)}
                {lookup.candidateCount === null ? "" : ` · ${lookup.candidateCount} results`}
                {lookup.latencyMs === null ? "" : ` · ${lookup.latencyMs} ms`}
                {` · ${lookup.actorKind}`}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
